/**
 * Tipos temporais branded.
 *
 * Sem brand, `setTimeout(fn, ms)` aceita qualquer número, mesmo "5 segundos"
 * que o caller pensou ser segundos mas a API espera ms. Brand força
 * conversão explícita via helpers (`seconds(5)` → `DurationMs`).
 */
import type { Brand } from '@planuze/core-kit';

/** Timestamp Unix em milliseconds (Date.now()). */
export type Timestamp = Brand<number, 'Timestamp'>;

/** ISO 8601 string (e.g., `'2026-05-08T13:00:00.000Z'`). */
export type IsoDateString = Brand<string, 'IsoDateString'>;

/** Duração em milliseconds. */
export type DurationMs = Brand<number, 'DurationMs'>;

/* ============== Factories e converters ============== */

export const now = (): Timestamp => Date.now() as Timestamp;
export const fromDate = (d: Date): Timestamp => d.getTime() as Timestamp;
export const toDate = (t: Timestamp): Date => new Date(t);
export const toIso = (t: Timestamp): IsoDateString => new Date(t).toISOString() as IsoDateString;
export const fromIso = (s: string): Timestamp => new Date(s).getTime() as Timestamp;

/* ============== Helpers de duração — sintaxe que se lê em prosa ============== */

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

export const durationOf = (ms: number): DurationMs => ms as DurationMs;
export const seconds = (n: number): DurationMs => (n * MS_PER_SECOND) as DurationMs;
export const minutes = (n: number): DurationMs => (n * MS_PER_MINUTE) as DurationMs;
export const hours = (n: number): DurationMs => (n * MS_PER_HOUR) as DurationMs;
export const days = (n: number): DurationMs => (n * MS_PER_DAY) as DurationMs;
